import { AxiosResponse } from 'axios'
import { MutationFunction, QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  Contact,
  ContactBulkAction,
  ContactListFilterOptions,
  ContactListFilters,
  ContactListItem,
  ContactParty,
  ContractFiltersRequest,
  ContractFiltersResponse,
  ContractLineItemsResponse,
  ContractReminder,
  ContractRequest,
  ContractResponse,
  ContractTypeListItemResponse,
  ContractTypeRequest,
  ContractTypeResponse,
  Finance,
  Organization,
  OrganizationListItem,
  OrganizationSettings,
  PaginatedContactsResponse,
} from '@models/ApproveitAccounting'
import { AccountingKeys } from '../Keys'

import { useCreate, useDelete, useGetList, useGetOne, usePatch, useUpdate } from '../request'
import api from '@src/api/api'
import { generatePath } from 'react-router-dom'
import { buildContactsFilterParams } from './filters'

export { buildContactsFilterParams } from './filters'

const PATHS = {
  organizations: '/api/accounting/organizations',
  contracts: '/api/accounting/contracts',
  contractTypes: '/api/accounting/contracts/agreements-types',
  contractsReminder: '/api/accounting/contracts/reminder',
  contacts: '/api/accounting/:contactParty',
  contactItem: '/api/accounting/:contactParty/:id',
  contactFinDetails: '/api/accounting/:contactParty/:contactId/financial-details',
  contactFinDetailsItem: '/api/accounting/:contactParty/:contactId/financial-details/:id',
  useVendorExport: '/api/accounting/:contactParty/export',
  terminateContract: '/api/accounting/contracts/:id/terminate',
  filters: '/api/accounting/contracts/filters',
  contactsBulkAction: '/api/accounting/:contactParty/bulk',
  contactsFilters: '/api/accounting/:contactParty/filters',
}

const getContactPartyUrlPart = (party: ContactParty) => {
  return party + 's'
}

export const useGetApproveitOrganizations = (enabled = true) =>
  useGetList<OrganizationListItem>([AccountingKeys.Organizations], PATHS.organizations, {
    query: {
      enabled,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  })

export const useGetApproveitOrganization = (id?: number | string) =>
  useGetOne<Organization>([AccountingKeys.Organizations, id], `${PATHS.organizations}/${id}`, {
    query: {
      enabled: !!id,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  })

export const useCreateApproveitOrganizations = () => {
  const cache = useQueryClient()
  return useCreate<Organization, Organization>(PATHS.organizations, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([AccountingKeys.Organizations])
      },
    },
  })
}
export const useUpdateApproveitOrganizations = (id?: number | null) => {
  const cache = useQueryClient()
  return usePatch<Partial<Omit<Organization, 'settings'> & { settings: Partial<OrganizationSettings> }>>(
    `${PATHS.organizations}/${id}`,
    {
      mutation: {
        onSuccess: () => {
          cache.invalidateQueries([AccountingKeys.Organizations])
        },
      },
    },
  )
}
export const useDeleteApproveitOrganizations = () => {
  const cache = useQueryClient()
  return useDelete<string | number>((id) => `${PATHS.organizations}/${id}`, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([AccountingKeys.Organizations])
      },
    },
  })
}

export const useGetContacts = (
  enabled = true,
  contactParty: ContactParty = ContactParty.vendor,
  requireNetsuite = false,
  netsuiteSubsidiary?: string,
) => {
  const path = generatePath(PATHS.contacts, {
    contactParty: getContactPartyUrlPart(contactParty),
  })
  const queryParams: Record<string, string> = {}
  if (requireNetsuite) queryParams.require_netsuite = 'true'
  if (netsuiteSubsidiary) queryParams.subsidiary = netsuiteSubsidiary

  return useGetList<ContactListItem>(
    [AccountingKeys.Contacts, contactParty, requireNetsuite ? 'netsuite' : 'all', netsuiteSubsidiary ?? 'any'],
    path,
    {
      query: {
        enabled,
      },
      axios: Object.keys(queryParams).length ? { params: queryParams } : undefined,
    },
  )
}

export interface PaginatedContactsParams {
  page: number
  size: number
  ordering?: string
  filters?: ContactListFilters
}

export const useGetContactsPaginated = (
  params: PaginatedContactsParams,
  contactParty: ContactParty = ContactParty.vendor,
  enabled = true,
) => {
  const queryParams = {
    page: params.page,
    size: params.size,
    ...(params.ordering ? { ordering: params.ordering } : {}),
    ...buildContactsFilterParams(params.filters),
  }
  const path = generatePath(PATHS.contacts, {
    contactParty: getContactPartyUrlPart(contactParty),
  })
  return useQuery<PaginatedContactsResponse>({
    queryKey: [AccountingKeys.ContactsPaginated, contactParty, queryParams],
    queryFn: async () => {
      const { data } = await api.get<PaginatedContactsResponse>(path, { params: queryParams })
      return data
    },
    enabled,
    // Keep the previous page visible while the next page is being fetched —
    // otherwise paging clears the table to "no records" mid-fetch.
    keepPreviousData: true,
  })
}

export const useGetContactsFilters = (contactParty: ContactParty = ContactParty.vendor, enabled = true) => {
  const path = generatePath(PATHS.contactsFilters, {
    contactParty: getContactPartyUrlPart(contactParty),
  })
  return useQuery<ContactListFilterOptions>({
    queryKey: [AccountingKeys.ContactsFilters, contactParty],
    queryFn: async () => {
      const { data } = await api.get<ContactListFilterOptions>(path)
      return data
    },
    enabled,
    // Facets change rarely (when an org/currency/country first appears on a
    // vendor). 60s is enough to skip the round-trip on rapid page paging.
    staleTime: 60_000,
  })
}

export const useGetContact = (id?: number | string | null, contactParty: ContactParty = ContactParty.vendor) => {
  const path = generatePath(PATHS.contactItem, {
    contactParty: getContactPartyUrlPart(contactParty),
    id: id || '',
  })
  return useGetOne<Contact>([AccountingKeys.Contacts, id?.toString()], path, {
    query: {
      enabled: !!id,
      cacheTime: Infinity,
      staleTime: Infinity,
      refetchOnWindowFocus: true,
    },
  })
}
export const useCreateContact = (contactParty: ContactParty = ContactParty.vendor) => {
  const cache = useQueryClient()
  const path = generatePath(PATHS.contacts, {
    contactParty: getContactPartyUrlPart(contactParty),
  })
  return useCreate<Contact, Contact>(path, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([AccountingKeys.Contacts, contactParty])
      },
    },
  })
}

type UpdateBody = Contact & {
  contactId: number
}
type UpdateContactContext = {
  previous: [QueryKey, PaginatedContactsResponse | undefined][]
}
export const useUpdateContact = (contactParty: ContactParty = ContactParty.vendor) => {
  const cache = useQueryClient()
  const listKey = [AccountingKeys.ContactsPaginated, contactParty]

  return useMutation<Contact, unknown, Partial<UpdateBody>, UpdateContactContext>({
    mutationFn: async ({ contactId, ...body }) => {
      const path = generatePath(PATHS.contactItem, {
        contactParty: getContactPartyUrlPart(contactParty),
        id: contactId || '',
      })
      return (await api.patch(path, body)).data
    },
    // Optimistically flip the toggled ``visible`` field in EVERY paginated
    // list cache entry so the controlled SwitchBox re-renders immediately,
    // independent of keepPreviousData refetch timing (the page 2+ bug where
    // the active page's previous data reference is served during the
    // background refetch, so the in-place toggle never shows). Patched in
    // place — the row is NOT removed even under an active ``visible`` filter;
    // the onSettled refetch reconciles row membership and ``count``. Scoped
    // to ``visible`` only so form-driven edits stay on the invalidation path.
    onMutate: async ({ contactId, visible }) => {
      if (visible === undefined) return { previous: [] }
      await cache.cancelQueries(listKey)
      const previous = cache.getQueriesData<PaginatedContactsResponse>(listKey)
      cache.setQueriesData<PaginatedContactsResponse>(listKey, (old) =>
        old ? { ...old, results: old.results.map((r) => (r.id === contactId ? { ...r, visible } : r)) } : old,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      context?.previous.forEach(([key, data]) => cache.setQueryData(key, data))
    },
    onSettled: (_data, _err, { contactId }) => {
      // Server reconciliation. Bust BOTH list caches. The unpaginated one
      // feeds every dropdown consumer (invoice filter, pipeline builder,
      // request form); the paginated one feeds the vendor list page and
      // recomputes pagination/``count`` after a filtered toggle. A single-row
      // PATCH is the same logical write as bulk update, just one-at-a-time,
      // so it has to invalidate the same caches the bulk endpoint does.
      cache.removeQueries([AccountingKeys.Contacts, contactId?.toString()])
      cache.invalidateQueries([AccountingKeys.Contacts, contactParty])
      cache.invalidateQueries([AccountingKeys.ContactsPaginated, contactParty])
    },
  })
}

export const useBulkContactUpdate = (contactParty: ContactParty = ContactParty.vendor) => {
  const cache = useQueryClient()
  return usePatch<ContactBulkAction>(
    generatePath(PATHS.contactsBulkAction, { contactParty: getContactPartyUrlPart(contactParty) }),
    {
      mutation: {
        onSuccess: () => {
          // Invalidate BOTH the unpaginated cache (used by every dropdown
          // consumer) AND the paginated list (used by the new vendor page).
          // Skipping the paginated key here would leave the table showing
          // stale visibility after a bulk action.
          cache.invalidateQueries([AccountingKeys.Contacts, contactParty])
          cache.invalidateQueries([AccountingKeys.ContactsPaginated, contactParty])
        },
      },
    },
  )
}

export const useDeleteContact = (contactParty: ContactParty = ContactParty.vendor) => {
  const cache = useQueryClient()
  return useDelete<Optional<string | number>>(
    (id) => {
      const path = generatePath(PATHS.contactItem, {
        contactParty: getContactPartyUrlPart(contactParty),
        id: id || '',
      })
      return path
    },
    {
      mutation: {
        onSuccess: () => {
          cache.invalidateQueries([AccountingKeys.Contacts, contactParty])
          cache.invalidateQueries([AccountingKeys.ContactsPaginated, contactParty])
        },
      },
    },
  )
}

export const useGetFinancialDetails = (
  contactId: number | null = null,
  contactParty: ContactParty = ContactParty.vendor,
) => {
  const path = generatePath(PATHS.contactFinDetails, {
    contactParty: getContactPartyUrlPart(contactParty),
    contactId: contactId || '',
  })
  return useGetList<Finance>([AccountingKeys.Finance, contactId], path, {
    query: {
      enabled: !!contactId,
    },
  })
}

type FinDetailsRequest = { contactId: number; data?: Finance; id?: number }
export const useCreateFinancialDetails = (contactParty: ContactParty = ContactParty.vendor) => {
  return useMutation<Finance, unknown, FinDetailsRequest>(async ({ data, contactId }: FinDetailsRequest) => {
    const path = generatePath(PATHS.contactFinDetails, {
      contactParty: getContactPartyUrlPart(contactParty),
      contactId: contactId,
    })
    return (await api.post(path, data)).data
  })
}

export const useUpdateFinancialDetails = (contactParty: ContactParty = ContactParty.vendor) => {
  return useMutation<unknown, unknown, FinDetailsRequest>(async ({ data, contactId, id }: FinDetailsRequest) => {
    const path = generatePath(PATHS.contactFinDetailsItem, {
      contactParty: getContactPartyUrlPart(contactParty),
      contactId: contactId,
      id,
    })
    return (await api.patch(path, data)).data
  })
}

export const useDeleteFinancialDetails = (contactParty: ContactParty = ContactParty.vendor) => {
  return useMutation<unknown, unknown, FinDetailsRequest>(async ({ contactId, id }: FinDetailsRequest) => {
    const path = generatePath(PATHS.contactFinDetailsItem, {
      contactParty: getContactPartyUrlPart(contactParty),
      contactId: contactId,
      id,
    })
    return (await api.delete(path)).data
  })
}

export const useImportVendorsFromCsv = (orgId?: number | string, party?: ContactParty) => {
  const cache = useQueryClient()
  const fileUpload: MutationFunction<string, File> = async (file: File) => {
    const form = new FormData()
    form.append('file', file)

    const res: AxiosResponse<string> = await api.post(
      `${PATHS.organizations}/${orgId}/import/${getContactPartyUrlPart(party || ContactParty.vendor)}`,
      form,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
    return res.data
  }

  return useMutation({
    mutationFn: fileUpload,
    onSuccess: () => {
      cache.invalidateQueries([AccountingKeys.Contacts, party])
    },
  })
}

export const useImportVendorsFromAccountingSoftware = (
  orgId?: number | string,
  contact?: string,
  party?: ContactParty,
) => {
  const cache = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      return (await api.get(`${PATHS.organizations}/${orgId}/sync-${party}s/${contact}`)).data
    },
    mutationKey: [AccountingKeys.VendorIntegrationSync],
    onSuccess: () => {
      cache.invalidateQueries([AccountingKeys.Contacts, party])
    },
  })
}

export const useVendorExport = (contactParty: ContactParty = ContactParty.vendor) => {
  return useMutation(
    async (params: {
      type: 'csv' | 'xlsx'
      organization?: number | string
      // When provided, the export honors the same filter context as the list
      // page — so "Export" matches what the user is looking at instead of
      // dumping the team's entire vendor pool.
      filters?: ContactListFilters
      // Explicit-selection mode: send a list of IDs and the backend exports
      // exactly those rows, ignoring filters. Standard Gmail/Linear semantics
      // (selected > filtered). When both are absent, the full filter-matching
      // population is exported.
      ids?: number[]
    }) => {
      const { filters, ids, ...rest } = params
      const queryParams: Record<string, unknown> = {
        ...rest,
        ...(ids && ids.length > 0 ? { ids: ids.join(',') } : buildContactsFilterParams(filters)),
      }
      const response = await api.get(
        generatePath(PATHS.useVendorExport, { contactParty: getContactPartyUrlPart(contactParty) }),
        {
          params: queryParams,
          responseType: 'blob',
        },
      )

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${contactParty}s_${new Date().toISOString()}.${params.type}`)
      document.body.appendChild(link)
      link.click()

      return null
    },
    {
      mutationKey: [AccountingKeys.VendorExport, contactParty],
    },
  )
}

export const useGetContracts = (filters: ContractFiltersRequest, enabled = true) => {
  const query = new URLSearchParams(JSON.parse(JSON.stringify(filters)))

  return useGetOne<ContractLineItemsResponse>([AccountingKeys.Contracts, query.toString()], PATHS.contracts, {
    query: {
      enabled,
      keepPreviousData: true,
    },
    axios: {
      params: filters,
    },
  })
}
export const useGetContractFilters = () => {
  return useGetOne<ContractFiltersResponse>([AccountingKeys.ContractFilters], PATHS.filters)
}

export const useGetContract = (id?: number | string | null) =>
  useGetOne<ContractResponse>([AccountingKeys.Contracts, id], `${PATHS.contracts}/${id}`, {
    query: {
      enabled: !!id,
    },
  })

export const useCreateContract = () => useCreate<ContractRequest>(PATHS.contracts)

export const useUpdateContract = (id?: number | string | null) => {
  const cache = useQueryClient()

  return usePatch<ContractRequest>(`${PATHS.contracts}/${id}`, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([AccountingKeys.Contracts, id])
      },
    },
  })
}

export const useDeleteContract = (id?: string | number) => {
  const cache = useQueryClient()
  return useDelete(`${PATHS.contracts}/${id}`, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([AccountingKeys.Contracts])
      },
    },
  })
}

export const useTerminateContract = () => {
  const cache = useQueryClient()
  return useCreate((id) => generatePath(PATHS.terminateContract, { id }), {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([AccountingKeys.Contracts])
      },
    },
  })
}

export const useGetContractReminder = () =>
  useGetOne<ContractReminder>([AccountingKeys.ContractsReminder], PATHS.contractsReminder)

export const useUpdateContractReminder = () => useUpdate<ContractReminder>(PATHS.contractsReminder)

export const useGetContractTypes = (enabled = true) =>
  useGetList<ContractTypeListItemResponse>([AccountingKeys.ContractTypes], PATHS.contractTypes, {
    query: {
      enabled,
    },
  })

export const useCreateContractType = () => {
  const cache = useQueryClient()
  return useCreate<ContractTypeRequest, ContractTypeResponse>(PATHS.contractTypes, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([AccountingKeys.ContractTypes])
      },
    },
  })
}
export const useUpdateContractType = (id?: number) => {
  const cache = useQueryClient()
  return usePatch<ContractTypeRequest, ContractTypeResponse>(`${PATHS.contractTypes}/${id}`, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([AccountingKeys.ContractTypes])
      },
    },
  })
}
export const useDeleteContractType = (id: number) => {
  const cache = useQueryClient()
  return useDelete(`${PATHS.contractTypes}/${id}`, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([AccountingKeys.ContractTypes])
      },
    },
  })
}
